import { all, call, delay, fork, put, takeLatest } from 'redux-saga/effects';

import * as Actions from './actions';
import * as ActionType from './constants';
import * as VpxActions from '../vpx/actions';
import {
  fetchBasicPostMethodWithToken,
  fetchGetMethod,
  fetchPostMethod,
  fetchPostMethodWithFormData,
  GetErrorMessageFromStatus,
} from '../../helpers/api';
import { getLoggedInUser } from '../../helpers/authUtils';
import { apiServiceUrl } from '../../constants';
import { buildRulesByPlan } from '../../helpers/subscriptionRules';

const userApiServiceUrl = `${apiServiceUrl}/user`;
const paymentApiServiceUrl = `${apiServiceUrl}/payment`;

function* updateProfilePassword({ payload: { currentPassword, newPassword } }) {
  try {
    const user = getLoggedInUser();
    const body = {
      CurrentPassword: currentPassword,
      NewPassword: newPassword,
    };

    const data = new FormData();
    data.append('CurrentPassword', currentPassword);
    data.append('NewPassword', newPassword);

    const response = yield call(
      fetchBasicPostMethodWithToken,
      `${userApiServiceUrl}/ChangePassword`,
      user.token,
      body,
      /* withoutResponseBody */ false
    );

    if (response.status === 200) {
      const { errorMessage, succeeded } = yield response.data
        .then((response) => {
          return {
            succeeded: response.succeeded,
            errorMessage: response.errorMessage,
          };
        })
        .catch(() => ({ status: 500 }));
      console.log('updateProfilePassword: ' + succeeded);
      if (succeeded) {
        yield put(Actions.updateProfilePasswordSuccess());
      } else {
        const message = errorMessage ?? 'Error trying to update password';
        yield put(Actions.updateProfilePasswordFailed(message));
      }
    } else {
      console.log('updateProfilePassword status <> 200');
      yield put(Actions.updateProfilePasswordFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    console.log('updateProfilePassword error: ' + error);
    yield put(Actions.updateProfilePasswordFailed(GetErrorMessageFromStatus(error)));
  }
}

function* loadProfileInformation() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${userApiServiceUrl}/LoadPersonalInformation`,
      user.token
    );

    if (response.status === 200) {
      const { profile, status } = yield response.data
        .then((response) => {
          return { profile: response.profile };
        })
        .catch(() => ({ status: 500 }));
      if (profile) {
        yield put(Actions.loadProfileInformationSuccess(profile));
      } else {
        yield put(
          Actions.loadProfileInformationFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.loadProfileInformationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadProfileInformationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* updateProfileInformation({
  payload: {
    name,
    companyName,
    phoneNumber,
    city,
    state,
    country,
    codeId,
    groupId,
    email,
    zipCode,
    address,
    logo,
  },
}) {
  const data = new FormData();
  data.append('Name', name);
  data.append('CompanyName', companyName);
  data.append('City', city);
  data.append('State', state);
  data.append('Country', country);
  data.append('PhoneNumber', phoneNumber ?? '');
  if (codeId != null) data.append('CodeId', codeId);
  if (groupId != null) data.append('GroupId', groupId);
  data.append('Email', email);
  data.append('ZipCode', zipCode ?? '');
  data.append('Address', address ?? '');
  if (logo instanceof File) {
    data.append('logo', logo);
  }
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethodWithFormData,
      `${userApiServiceUrl}/SavePersonalInformation`,
      user.token,
      data
    );

    if (response.status === 200) {
      const { profile, status } = yield response.data
        .then((response) => {
          return { profile: response.profile };
        })
        .catch(() => ({ status: 500 }));
      if (profile) {
        yield put(Actions.updateProfileInformationSuccess(profile));
        const savedLogo = profile.logo ?? profile.Logo;
        if (savedLogo) {
          yield put(VpxActions.syncUserProfileOwnerAvatar(savedLogo));
        }
      } else {
        yield put(
          Actions.updateProfileInformationFailed(GetErrorMessageFromStatus({ status: status }))
        );
      }
    } else {
      yield put(Actions.updateProfileInformationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.updateProfileInformationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* loadUserSubscriptionInformation() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${paymentApiServiceUrl}/GetSubscriptions`,
      user.token
    );

    if (response.status === 200) {
      const { subscriptions, status } = yield response.data
        .then((response) => {
          // Debug: Log the API response to see if billingInterval is present
          console.log('[Saga] GetSubscriptions API Response:', response);
          if (Array.isArray(response)) {
            response.forEach((plan, index) => {
              console.log(`[Saga] Plan ${index + 1}:`, {
                id: plan.id,
                name: plan.name,
                isCurrent: plan.isCurrent,
                billingInterval: plan.billingInterval || plan.BillingInterval || 'NOT FOUND'
              });
            });
          }
          return { subscriptions: response };
        })
        .catch(() => ({ status: 500 }));
      if (subscriptions) {
        yield put(Actions.loadUserSubscriptionInformationSuccess(subscriptions));
      } else {
        yield put(
          Actions.loadUserSubscriptionInformationFailed(
            GetErrorMessageFromStatus({ status: status })
          )
        );
      }
    } else {
      yield put(Actions.loadUserSubscriptionInformationFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.loadUserSubscriptionInformationFailed(GetErrorMessageFromStatus(error)));
  }
}

function* cancelSubscription({ payload: { newPlanId } }) {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethod,
      `${paymentApiServiceUrl}/CancelSubscription`,
      user.token,
      {}
    );

    if (response.status === 200) {
      const responseData = yield response.data;
      const message = responseData?.message || responseData?.Message || 'Subscription cancelled successfully';
      yield put(Actions.cancelSubscriptionSuccess(message, newPlanId));
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response }));
      yield put(Actions.cancelSubscriptionFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.cancelSubscriptionFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.cancelSubscriptionFailed(GetErrorMessageFromStatus(error)));
  }
}

function* upgradeOrDowngradeSubscription({ payload: { newPlanId, billingInterval } }) {
  try {
    const user = getLoggedInUser();
    // Send both newPlanId and billingInterval
    // newPlanId: 2 for Premium, 3 for Executive
    // billingInterval: "month" or "year" (defaults to "month" if not provided)
    const requestBody = { 
      newPlanId: newPlanId || null,
      billingInterval: billingInterval || null 
    };
    
    console.log('[upgradeOrDowngradeSubscription] Sending request:', {
      newPlanId,
      billingInterval,
      requestBody
    });
    
    // Use fetchBasicPostMethodWithToken to send JSON instead of FormData
    const response = yield call(
      fetchBasicPostMethodWithToken,
      `${paymentApiServiceUrl}/UpgradeOrDowngradeSubscription`,
      user.token,
      requestBody
    );

    if (response.status === 200) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      if (message) {
        yield put(Actions.upgradeOrDowngradeSubscriptionSuccess(message, newPlanId));
      } else {
        yield put(
          Actions.upgradeOrDowngradeSubscriptionFailed(GetErrorMessageFromStatus(response))
        );
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response }));
      yield put(
        Actions.upgradeOrDowngradeSubscriptionFailed(message ?? GetErrorMessageFromStatus(response))
      );
    } else {
      yield put(Actions.upgradeOrDowngradeSubscriptionFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.upgradeOrDowngradeSubscriptionFailed(GetErrorMessageFromStatus(error)));
  }
}

function* updateSuccessfulPlan({ payload: { newPlanId } }) {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchPostMethod,
      `${paymentApiServiceUrl}/UpdateSuccessfulPlan`,
      user.token,
      {}
    );

    if (response.status === 200) {
      const { message } = yield response.data.then((response) => ({ message: response.message }));
      if (message) {
        yield put(Actions.updateSuccessfulPlanSuccess(message, newPlanId));
        yield put(Actions.loadUserSubscriptionInformation());
      } else {
        yield put(Actions.updateSuccessfulPlanFailed(GetErrorMessageFromStatus(response)));
      }
    } else if (response.status === 400) {
      const { message } = yield response.data.then((response) => ({ message: response }));
      yield put(Actions.updateSuccessfulPlanFailed(message ?? GetErrorMessageFromStatus(response)));
    } else {
      yield put(Actions.updateSuccessfulPlanFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.updateSuccessfulPlanFailed(GetErrorMessageFromStatus(error)));
  }
}

export function* watchUpdateProfilePassword(): any {
  yield takeLatest(ActionType.UPDATE_PROFILE_PASSWORD, updateProfilePassword);
}

export function* watchLoadProfileInformation(): any {
  yield takeLatest(ActionType.LOAD_PROFILE_INFORMATION, loadProfileInformation);
}

export function* watchUpdateProfileInformation(): any {
  yield takeLatest(ActionType.UPDATE_PROFILE_INFORMATION, updateProfileInformation);
}

export function* watchLoadUserSubscriptionInformation(): any {
  yield takeLatest(ActionType.LOAD_USER_SUBSCRIPTION_INFORMATION, loadUserSubscriptionInformation);
}

export function* watchCancelSubscription(): any {
  yield takeLatest(ActionType.CANCEL_SUBSCRIPTION, cancelSubscription);
}

export function* watchUpgradeOrDowngradeSubscription(): any {
  yield takeLatest(ActionType.UPGRADE_OR_DOWNGRADE_SUBSCRIPTION, upgradeOrDowngradeSubscription);
}

export function* watchUpdateSuccessfulPlan(): any {
  yield takeLatest(ActionType.UPDATE_SUCCESSFUL_PLAN, updateSuccessfulPlan);
}

function* getMySubscriptionRules() {
  try {
    const user = getLoggedInUser();
    const response = yield call(
      fetchGetMethod,
      `${paymentApiServiceUrl}/GetMySubscriptionRules`,
      user.token
    );

    if (response.status === 200) {
      const { subscriptionRules, status } = yield response.data
        .then((response) => {
          return { subscriptionRules: response };
        })
        .catch(() => ({ status: 500 }));
      if (subscriptionRules) {
        yield put(Actions.getMySubscriptionRulesSuccess(subscriptionRules));
      } else {
        yield put(
          Actions.getMySubscriptionRulesFailed(
            GetErrorMessageFromStatus({ status: status })
          )
        );
      }
    } else {
      yield put(Actions.getMySubscriptionRulesFailed(GetErrorMessageFromStatus(response)));
    }
  } catch (error) {
    yield put(Actions.getMySubscriptionRulesFailed(GetErrorMessageFromStatus(error)));
  }
}

function* watchGetMySubscriptionRules(): any {
  yield takeLatest(ActionType.GET_MY_SUBSCRIPTION_RULES, getMySubscriptionRules);
}

function* loadSubscriptionRules() {
  try {
    const response = yield call(fetch, `${paymentApiServiceUrl}/GetSubscriptionRules`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
    });

    if (response.status === 200) {
      const body = yield response.json();
      yield put(Actions.loadSubscriptionRulesSuccess(buildRulesByPlan(body)));
    } else {
      yield put(
        Actions.loadSubscriptionRulesFailed(
          GetErrorMessageFromStatus({ status: response.status })
        )
      );
    }
  } catch (error) {
    yield put(Actions.loadSubscriptionRulesFailed(GetErrorMessageFromStatus(error)));
  }
}

function* watchLoadSubscriptionRules(): any {
  yield takeLatest(ActionType.LOAD_SUBSCRIPTION_RULES, loadSubscriptionRules);
}

function* userSaga(): any {
  yield all([
    fork(watchUpdateProfilePassword),
    fork(watchLoadProfileInformation),
    fork(watchUpdateProfileInformation),
    fork(watchLoadUserSubscriptionInformation),
    fork(watchCancelSubscription),
    fork(watchUpgradeOrDowngradeSubscription),
    fork(watchUpdateSuccessfulPlan),
    fork(watchGetMySubscriptionRules),
    fork(watchLoadSubscriptionRules),
  ]);
}

export default userSaga;
